import { ChartInfoKind } from '@frontend/chart/types';
import { EntityType } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage, useIntl } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { StatisticsCard } from '../../shared';
import { formatIotTypeLabel } from '../format-iot-type-label';
import { getIotStatusDisplayLabel } from '../iot-status-display';
import { getIotDisplayName, useHardwareCriticalIotsListCard } from './use-hardware-critical-iots-list-card';

export interface HardwareCriticalIotsListCardProps {
    accountId: string;
}

export const HardwareCriticalIotsListCard = (props: HardwareCriticalIotsListCardProps) => {
    const intl = useIntl();
    const { items, isLoading } = useHardwareCriticalIotsListCard(props);
    const isEmpty = !isLoading && items.length === 0;

    return (
        <StatisticsCard
            infoKind={ChartInfoKind.CRITICAL_IOTS_LIST}
            title={
                <FormattedMessage
                    id='platform.statistics.hardware.hardware-critical-iots-list-card.title'
                    description='Title of the hardware dashboard list showing IoTs that are currently in a critical status'
                    defaultMessage='Critical IoTs'
                />
            }
            subtitle={
                isEmpty ? (
                    <FormattedMessage
                        id='platform.statistics.hardware.hardware-critical-iots-list-card.subtitle-ok'
                        description='Subtitle on the hardware critical IoTs card when no assets need attention'
                        defaultMessage='All clear'
                    />
                ) : (
                    <FormattedMessage
                        id='platform.statistics.hardware.hardware-critical-iots-list-card.subtitle'
                        description='Subtitle of the hardware critical IoTs list indicating these assets need attention now'
                        defaultMessage='Needs attention now'
                    />
                )
            }
            isLoading={isLoading}
            hasData>
            {isEmpty ? (
                <div className='text-foreground-muted flex min-h-0 w-full flex-1 items-center justify-center px-4 text-center text-base font-medium'>
                    <FormattedMessage
                        id='platform.statistics.hardware.hardware-critical-iots-list-card.empty'
                        description='Empty state on the hardware critical IoTs list when every IoT is healthy'
                        defaultMessage='Everything is working fine'
                    />
                </div>
            ) : (
                <div className='min-h-0 w-full min-w-0 flex-1 overflow-auto'>
                    <div className='min-w-[640px]'>
                        <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                            <div className='min-w-0 flex-1 px-4 py-3'>{CommonMessage.OBJECTS.IOT.ASSET.DEFAULT}</div>
                            <div className='w-[100px] shrink-0 px-4 py-3'>
                                <FormattedMessage
                                    id='platform.statistics.hardware.hardware-critical-iots-list-card.status-header'
                                    description='Column header for IoT status on the hardware critical IoTs list'
                                    defaultMessage='Status'
                                />
                            </div>
                            <div className='w-[140px] shrink-0 px-4 py-3'>{CommonMessage.FORMS.TYPE}</div>
                            <div className='w-[160px] shrink-0 px-4 py-3'>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}</div>
                        </div>
                        {items.map((iot) => (
                            <div
                                key={iot.id}
                                className='border-border-default flex h-11 items-center gap-4 border-b'>
                                <div className='flex min-w-0 flex-1 items-center overflow-hidden px-4'>
                                    <PlatformObjectBadge
                                        entityType={EntityType.IOT}
                                        id={iot.id}
                                        display={getIotDisplayName(iot)}
                                    />
                                </div>
                                <div className='w-[100px] shrink-0 px-4'>
                                    <span className='text-background-surface bg-status-danger inline-flex max-w-full truncate rounded-full px-2 py-0.5 text-sm font-semibold'>
                                        {getIotStatusDisplayLabel(iot.status ?? 'none', intl)}
                                    </span>
                                </div>
                                <div className='text-foreground-default w-[140px] shrink-0 truncate px-4 text-sm font-medium'>
                                    {formatIotTypeLabel(iot.type)}
                                </div>
                                <div className='w-[160px] shrink-0 overflow-hidden px-4'>
                                    {iot.spot ? (
                                        <PlatformObjectBadge
                                            entityType={EntityType.SPOT}
                                            id={iot.spot.entity_id}
                                            display={iot.spot.name}
                                            foreign={iot.spot.foreign}
                                            accountId={accountIdFromResolvedEntity(iot.spot)}
                                        />
                                    ) : (
                                        <span className='text-foreground-muted'>/</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </StatisticsCard>
    );
};
